import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faFolderOpen, faEye, faCheck, faXmark, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import {
  authFetch,
  formatDate,
  openAuthedFile,
  DOCUMENT_TYPE_LABELS,
  DOCUMENT_DISPLAY_ORDER,
  DOCUMENT_STATUS_BADGE,
  DOCUMENT_STATUS_LABELS,
} from '../../utils/http';

interface Doc {
  id: string;
  documentType: number;
  fileName: string;
  fileSizeBytes: number;
  status: number;
  reviewNote: string | null;
  uploadedAt: string;
}

interface StudentRow {
  studentId: string;
  studentName: string;
  studentIdNumber: string;
  email: string;
  documents: Doc[];
}

/** Admin: view and verify learners' ID, matric and affidavit uploads. Trainers have no access to these. */
export default function AdminStudentDocumentsPage() {
  const { accessToken } = useAuth();
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [onlyPending, setOnlyPending] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await authFetch<StudentRow[]>(accessToken, '/api/student-documents/overview');
    if (res.ok && res.data) {
      setStudents(res.data);
      setError('');
    } else {
      setError(res.message || 'Failed to load student documents.');
    }
    setIsLoading(false);
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const view = async (id: string) => {
    const err = await openAuthedFile(accessToken, `/api/student-documents/${id}/file`);
    if (err) setError(err);
  };

  const review = async (id: string, verify: boolean) => {
    setBusyId(id);
    const res = await authFetch(accessToken, `/api/student-documents/${id}/review`, {
      method: 'POST',
      body: { verify, note: notes[id] ?? '' },
    });
    setBusyId(null);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setError('');
    load();
  };

  const q = search.trim().toLowerCase();
  const rows = students.filter((s) => {
    if (onlyPending && !s.documents.some((d) => d.status === 0)) return false;
    return !q || s.studentName.toLowerCase().includes(q) || s.studentIdNumber.toLowerCase().includes(q) || s.email.toLowerCase().includes(q);
  });

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Student Documents</h1>
        <p className="text-sm text-brand-900/60 mt-1">Matric, ID copy, CV and SETA affidavit uploads. Visible to admins and the learner only.</p>
      </div>

      <div className="p-8 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <FontAwesomeIcon icon={faSearch} className="h-3.5 w-3.5 text-brand-900/40 absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, student number or email"
              className="w-full pl-9 pr-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-brand-900/70">
            <input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} />
            Awaiting review
          </label>
        </div>

        {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faFolderOpen} className="h-12 w-12 text-brand-300 mb-4" />
            <p className="text-brand-900/60">No students match.</p>
          </div>
        ) : (
          <div className="card-surface divide-y divide-canvas-200 overflow-hidden">
            {rows.map((s) => {
              const isOpen = expanded === s.studentId;
              return (
                <div key={s.studentId}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : s.studentId)}
                    className="w-full text-left px-5 py-4 hover:bg-canvas-50 transition-colors flex flex-col md:flex-row md:items-center md:justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-brand-950">{s.studentName}</p>
                      <p className="text-xs text-brand-900/50">
                        {s.studentIdNumber} · {s.email}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {DOCUMENT_DISPLAY_ORDER.map((type) => {
                        const label = DOCUMENT_TYPE_LABELS[type];
                        const d = s.documents.find((x) => x.documentType === type);
                        return (
                          <span
                            key={type}
                            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${d ? DOCUMENT_STATUS_BADGE[d.status] : 'bg-gray-100 text-gray-600'}`}
                          >
                            {label}: {d ? DOCUMENT_STATUS_LABELS[d.status] : 'Missing'}
                          </span>
                        );
                      })}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-5 pb-5 space-y-3 bg-canvas-50">
                      {s.documents.length === 0 && <p className="text-sm text-brand-900/60 pt-3">This learner has not uploaded anything yet.</p>}
                      {s.documents.map((d) => (
                        <div key={d.id} className="bg-white border border-canvas-300 rounded-xl p-4 mt-3">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-brand-950">{DOCUMENT_TYPE_LABELS[d.documentType]}</p>
                              <p className="text-xs text-brand-900/50">
                                {d.fileName} · uploaded {formatDate(d.uploadedAt)}
                              </p>
                              {d.reviewNote && <p className="text-xs text-brand-900/70 mt-1">Note: {d.reviewNote}</p>}
                            </div>
                            <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${DOCUMENT_STATUS_BADGE[d.status]}`}>
                              {DOCUMENT_STATUS_LABELS[d.status]}
                            </span>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-2 mt-3">
                            <button
                              onClick={() => view(d.id)}
                              className="px-3 py-1.5 border border-canvas-300 text-brand-900 text-xs font-semibold rounded-lg hover:bg-canvas-100 transition-colors flex items-center justify-center gap-1.5"
                            >
                              <FontAwesomeIcon icon={faEye} className="h-3 w-3" />
                              View
                            </button>
                            <input
                              type="text"
                              value={notes[d.id] ?? ''}
                              onChange={(e) => setNotes((prev) => ({ ...prev, [d.id]: e.target.value }))}
                              placeholder="Note (required when rejecting)"
                              className="flex-1 px-3 py-1.5 border border-canvas-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-500"
                            />
                            <button
                              onClick={() => review(d.id, true)}
                              disabled={busyId === d.id}
                              className="px-3 py-1.5 bg-brand-gradient text-white text-xs font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-1.5"
                            >
                              <FontAwesomeIcon icon={faCheck} className="h-3 w-3" />
                              Verify
                            </button>
                            <button
                              onClick={() => review(d.id, false)}
                              disabled={busyId === d.id}
                              className="px-3 py-1.5 border border-red-200 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                            >
                              <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
                              Reject
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
